import { useCallback } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import * as yup from 'yup';

import { desksEndpoints } from '@/api/endpoints';
import { DEFAULT_HOURS, extractIntervalsFromHours } from '@/components/pages/BusinessHours/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { API as businessHoursApi } from '@/hooks/useBusinessHours';
import useDesks from '@/hooks/useDesks';
import { popModal } from '@/redux/modals/actions';
import { selectAccountSlug } from '@/redux/session/selectors';
import { getUserTimezone } from '@/util/dateUtils';
import { extractNames, generateUniqueName } from '@/util/util';
import { LENGTH_M } from '@/util/validation/constants';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

const ModalDeskCreation = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { createDesk, createStatus, desks } = useDesks();
  const slug = useSelector(selectAccountSlug);
  const dispatch = useDispatch();

  const formSchema = yup.object({
    name: createNameFieldSchema({ getEndpoint: desksEndpoints.checkName }),
    description: yup.string().max(LENGTH_M).default(''),
    is_service_desk: yup.boolean().required().default(true),
  });

  type FormValues = yup.InferType<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: yupResolver<FormValues, unknown, FormValues>(formSchema),
    mode: 'onChange',
    defaultValues: { name: '', description: '', is_service_desk: true },
  });

  const handleClose = useCallback(() => {
    dispatch(popModal());
  }, [dispatch]);

  const onSubmit = useCallback(
    (values: FormValues) => {
      createDesk(
        {
          name: generateUniqueName(values.name, extractNames(desks)),
          description: values.description || '',
          is_service_desk: values.is_service_desk,
        },
        {
          onSuccess: (desk) => {
            navigate(`/${slug}/environments/${desk.desk_id}`);
            businessHoursApi.addBusinessHour(desk.desk_id, {
              name: t('business_hours.default'),
              timezone: getUserTimezone(),
              is_default: true,
              intervals: extractIntervalsFromHours([DEFAULT_HOURS]),
              holidays: [],
            });
            handleClose();
          },
        }
      );
    },
    [createDesk, desks, handleClose, navigate, slug, t]
  );

  return (
    <Dialog open onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('environments.modal.create_title')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} autoComplete="off" className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('common.name')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('common.name_placeholder')} autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('field.description')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('common.description_placeholder')}
                      maxLength={LENGTH_M}
                      rows={2}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_service_desk"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-start gap-3">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="grid gap-1">
                      <FormLabel className="text-sm leading-none font-medium" htmlFor="terms-2">
                        {t('environments.modal.human_chat')}
                      </FormLabel>
                      <p className="text-sm text-foreground-muted">
                        {t('environments.modal.human_chat_subtitle')}
                      </p>
                    </div>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="ghost-neutral" onClick={handleClose}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" isLoading={createStatus === 'pending'}>
                {t('common.create')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ModalDeskCreation;
